// Procedural weapon models: chunky low-poly guns with a distinct silhouette per class.
//
// Weapon local space: +X right, +Y up, -Z forward (muzzle), origin at the trigger. Static parts are
// merged per material key (body / trim / metal / accent / lens / emit); the magazine is a separate
// group so the viewmodel can animate it. Every anchor is a direct child of `root`.
//
//   muzzle        where flash and tracers start (end of the barrel or muzzle device)
//   sightAnchor   centre of the rear sight aperture or optic rear window on the sight line. While aiming
//                 the camera sits at sightAnchor + (0, 0, eyeRelief); the viewmodel aligns on it.
//   rightHandGrip centre of the object the right hand wraps (pistol grip, or the knife handle);
//   leftHandGrip  centre of the object the left hand wraps (handguard / grip). `userData.hold` on both
//                 anchors tells hands.ts how to wrap: "grip" (vertical), "cup" (around a horizontal
//                 handguard), "handle" (knife) or "none".

import * as THREE from "three";
import { MeshBuilder, disposeGeometries } from "./geom";
import type { V2, V3 } from "./geom";
import {
  barrelDelta,
  buildGrip,
  buildLightStock,
  buildMuzzle,
  buildOptic,
  magVariant,
  parseAttachments,
} from "./attachments";
import type { AttachmentSet, Mounts } from "./attachments";
import { createWeaponMaterials } from "../materials/materials";
import type { WeaponMaterialKey } from "../materials/materials";

export type HandHold = "grip" | "cup" | "handle" | "none";

export interface WeaponModel {
  id: string;
  root: THREE.Group;
  muzzle: THREE.Object3D;
  /** Separate group for reload animation; its origin is the top of the magazine. Null for weapons without one. */
  magazine: THREE.Object3D | null;
  sightAnchor: THREE.Object3D;
  leftHandGrip: THREE.Object3D;
  rightHandGrip: THREE.Object3D;
  /** True for the sniper rifle and the 4x scope (draw a scope overlay at full ADS). */
  hasScopeOverlay: boolean;
  /** False for the knife (the left hand is hidden). */
  twoHanded: boolean;
  dispose(): void;
}

export const WEAPON_IDS: readonly string[] = [
  "vx9",
  "kr7",
  "br3",
  "hx12",
  "dm2",
  "lx44",
  "p17",
  "vp4",
  "hc50",
  "knife",
];

/** Pure description of an assembled weapon (no GPU objects): used by tests and the viewer. */
export interface WeaponLayout {
  rightGrip: V3;
  rightHold: HandHold;
  leftGrip: V3;
  leftHold: HandHold;
  muzzle: V3;
  sight: V3;
  magPivot: V3 | null;
  hasScopeOverlay: boolean;
  twoHanded: boolean;
}

export interface WeaponDescription {
  layout: WeaponLayout;
  /** Triangles of the merged static parts plus the magazine. */
  triangles: number;
}

interface Iron {
  rearZ: number;
  rearBaseY: number;
  frontZ: number;
  frontBaseY: number;
  sightY: number;
}

interface SpecResult {
  mounts: Mounts | null;
  iron: Iron | null;
  /** Built-in optic (sniper scope): sight anchor position. */
  builtinSight: { y: number; z: number } | null;
  rightGrip: V3;
  rightHold: HandHold;
  leftGrip: V3;
  leftHold: HandHold;
  magPivot: V3 | null;
  /** Overrides the default muzzle position (knife tip). */
  muzzle?: V3;
}

type Spec = (b: MeshBuilder, mb: MeshBuilder, set: AttachmentSet) => SpecResult;

interface Assembly {
  body: MeshBuilder;
  mag: MeshBuilder;
  layout: WeaponLayout;
}

// ---------------------------------------------------------------------------------------------
// Shared part helpers

/** Flip-up style iron sights: rear wings on both sides of the sight line, front post tip at sightY. */
function ironSights(b: MeshBuilder, s: Iron): void {
  const wingH = s.sightY + 0.012 - s.rearBaseY;
  b.box("trim", [0.007, wingH, 0.012], [0.011, s.rearBaseY + wingH / 2, s.rearZ]);
  b.box("trim", [0.007, wingH, 0.012], [-0.011, s.rearBaseY + wingH / 2, s.rearZ]);
  b.box("trim", [0.03, 0.006, 0.014], [0, s.rearBaseY + 0.003, s.rearZ]);
  const postH = Math.max(s.sightY - s.frontBaseY, 0.008);
  b.box("accent", [0.005, postH, 0.006], [0, s.frontBaseY + postH / 2, s.frontZ]);
  b.box("trim", [0.016, 0.01, 0.016], [0, s.frontBaseY + 0.005, s.frontZ]);
}

/** Trigger guard: front bar and bottom bar. */
function guard(b: MeshBuilder, topY: number, botY: number, frontZ: number, backZ: number): void {
  b.box("trim", [0.01, topY - botY, 0.008], [0, (topY + botY) / 2, frontZ]);
  b.box("trim", [0.01, 0.008, backZ - frontZ], [0, botY + 0.004, (frontZ + backZ) / 2]);
}

function trigger(b: MeshBuilder, y: number, z: number): void {
  b.box("metal", [0.008, 0.026, 0.008], [0, y, z]);
}

/** Dark dashes along a picatinny rail top. */
function railSlots(b: MeshBuilder, topY: number, z0: number, z1: number, n: number): void {
  for (let i = 0; i < n; i++) {
    b.box("trim", [0.0265, 0.002, 0.006], [0, topY, z0 + ((z1 - z0) * i) / (n - 1)]);
  }
}

/** Box magazine hanging from the pivot (top of the magazine). */
function stickMag(mb: MeshBuilder, width: number, depth: number, len: number, fast: boolean): void {
  mb.box("trim", [width, len, depth], [0, -len / 2, 0]);
  mb.box(fast ? "accent" : "trim", [width + 0.004, 0.01, depth + 0.006], [0, -len - 0.005, 0]);
  if (fast) mb.box("accent", [0.012, 0.018, 0.008], [0, -len - 0.019, depth / 2 - 0.004]);
}

/** Pistol grip prism in side view. */
function gripPrism(b: MeshBuilder, pts: ReadonlyArray<V2>, width: number, key = "trim"): void {
  b.prism(key, pts, width, [0, 0, 0]);
}

// ---------------------------------------------------------------------------------------------
// Weapon specs

const vx9: Spec = (b, mb, set) => {
  const ext = barrelDelta(set);
  const mv = magVariant(set);
  const tip = -0.46 - ext;
  b.box("body", [0.05, 0.09, 0.34], [0, 0.005, -0.03], { bevel: 0.006 });
  b.box("body", [0.026, 0.012, 0.3], [0, 0.056, -0.04]);
  railSlots(b, 0.062, -0.17, 0.09, 8);
  b.box("trim", [0.004, 0.022, 0.05], [0.026, 0.025, -0.02]);
  b.box("body", [0.058, 0.075, 0.17], [0, 0.012, -0.285], { bevel: 0.006 });
  for (const z of [-0.24, -0.285, -0.33]) b.box("trim", [0.06, 0.02, 0.012], [0, 0.012, z]);
  const len = Math.max(0.09 + ext, 0.03);
  b.cyl("metal", "z", len, 0.011, 0.011, [0, 0.015, -0.37 - len / 2]);
  if (!set.muzzle) b.cyl("metal", "z", 0.02, 0.0165, 0.0165, [0, 0.015, tip + 0.01]);
  guard(b, -0.04, -0.085, -0.07, 0);
  trigger(b, -0.055, -0.01);
  gripPrism(b, [[-0.01, -0.04], [0.05, -0.04], [0.07, -0.15], [0.015, -0.15]], 0.034);
  b.box("body", [0.04, 0.02, 0.058], [0, -0.05, -0.075]);
  stickMag(mb, 0.03, 0.048, 0.15 * mv.lengthScale, mv.fast);
  const mounts: Mounts = {
    railTop: 0.062,
    opticZ: -0.02,
    barrelY: 0.015,
    barrelR: 0.011,
    muzzleZ: tip,
    underY: -0.0255,
    underZ: -0.285,
    stockZ: 0.14,
    stockY: 0,
  };
  if (set.grip === "light_stock") {
    buildLightStock(b, mounts);
  } else {
    b.box("metal", [0.008, 0.008, 0.17], [0.018, 0.02, 0.225]);
    b.box("metal", [0.008, 0.008, 0.17], [-0.018, 0.02, 0.225]);
    b.box("metal", [0.008, 0.008, 0.15], [0, -0.03, 0.215]);
    b.box("body", [0.05, 0.1, 0.02], [0, 0, 0.32], { bevel: 0.004 });
  }
  return {
    mounts,
    iron: { rearZ: 0.09, rearBaseY: 0.062, frontZ: -0.355, frontBaseY: 0.0495, sightY: 0.092 },
    builtinSight: null,
    rightGrip: [0, -0.09, 0.035],
    rightHold: "grip",
    leftGrip: [0, 0.012, -0.285],
    leftHold: "cup",
    magPivot: [0, -0.06, -0.075],
  };
};

const kr7: Spec = (b, mb, set) => {
  const ext = barrelDelta(set);
  const mv = magVariant(set);
  const len = Math.max(0.15 + ext, 0.04);
  const tip = -0.46 - len;
  b.box("body", [0.05, 0.07, 0.28], [0, 0.025, -0.06], { bevel: 0.006 });
  b.box("body", [0.048, 0.075, 0.17], [0, -0.03, 0], { bevel: 0.006 });
  b.box("body", [0.024, 0.012, 0.54], [0, 0.066, -0.19]);
  railSlots(b, 0.072, -0.4, 0.06, 12);
  b.box("trim", [0.004, 0.022, 0.05], [0.0255, 0.03, -0.03]);
  b.box("body", [0.06, 0.075, 0.26], [0, 0.022, -0.33], { bevel: 0.007 });
  for (const z of [-0.26, -0.3, -0.34, -0.38, -0.42]) b.box("trim", [0.062, 0.018, 0.012], [0, 0.022, z]);
  b.cyl("metal", "z", len, 0.0095, 0.0095, [0, 0.022, -0.46 - len / 2]);
  b.box("metal", [0.02, 0.026, 0.03], [0, 0.022, -0.5]);
  if (!set.muzzle) b.cyl("metal", "z", 0.045, 0.0135, 0.0135, [0, 0.022, tip + 0.0225], { segments: 6 });
  guard(b, -0.0675, -0.1, -0.075, 0.025);
  trigger(b, -0.08, -0.01);
  gripPrism(b, [[0.02, -0.0675], [0.075, -0.0675], [0.09, -0.17], [0.035, -0.17]], 0.034);
  const shift = 0.03 * mv.lengthScale;
  const mlen = 0.16 * mv.lengthScale;
  mb.prism(
    "trim",
    [[0.019, 0], [-0.019, 0], [-0.019 - shift, -mlen], [0.019 - shift, -mlen]],
    0.03,
    [0, 0, 0],
  );
  mb.box(mv.fast ? "accent" : "trim", [0.034, 0.01, 0.044], [0, -mlen - 0.005, -shift]);
  if (mv.fast) mb.box("accent", [0.012, 0.018, 0.008], [0, -mlen - 0.019, -shift + 0.016]);
  const mounts: Mounts = {
    railTop: 0.072,
    opticZ: -0.05,
    barrelY: 0.022,
    barrelR: 0.0095,
    muzzleZ: tip,
    underY: -0.0155,
    underZ: -0.36,
    stockZ: 0.085,
    stockY: 0,
  };
  b.cyl("metal", "z", 0.16, 0.0165, 0.0165, [0, 0.015, 0.165]);
  if (set.grip === "light_stock") {
    buildLightStock(b, mounts);
  } else {
    b.prism("body", [[0.1, 0.045], [0.31, 0.05], [0.315, -0.09], [0.27, -0.1], [0.13, -0.02]], 0.04, [0, 0, 0]);
    b.box("trim", [0.044, 0.14, 0.014], [0, -0.02, 0.32], { bevel: 0.003 });
  }
  return {
    mounts,
    iron: { rearZ: 0.06, rearBaseY: 0.072, frontZ: -0.5, frontBaseY: 0.035, sightY: 0.102 },
    builtinSight: null,
    rightGrip: [0, -0.12, 0.057],
    rightHold: "grip",
    leftGrip: [0, 0.022, -0.33],
    leftHold: "cup",
    magPivot: [0, -0.0675, -0.035],
  };
};

/** Bullpup burst rifle: the magazine sits behind the pistol grip, boxy angular shell. */
const br3: Spec = (b, mb, set) => {
  const ext = barrelDelta(set);
  const mv = magVariant(set);
  const len = Math.max(0.15 + ext, 0.04);
  const tip = -0.46 - len;
  b.prism(
    "body",
    [[-0.3, 0.066], [0.2, 0.07], [0.3, 0.04], [0.3, -0.09], [0.12, -0.085], [-0.3, -0.03]],
    0.052,
    [0, 0, 0],
  );
  b.box("body", [0.024, 0.01, 0.48], [0, 0.071, -0.06]);
  railSlots(b, 0.076, -0.28, 0.17, 10);
  b.box("body", [0.054, 0.075, 0.16], [0, 0.0375, -0.38], { bevel: 0.006 });
  for (const z of [-0.42, -0.38, -0.34]) b.box("trim", [0.056, 0.018, 0.01], [0, 0.0375, z]);
  b.cyl("metal", "z", len, 0.0105, 0.0105, [0, 0.0375, -0.46 - len / 2]);
  if (!set.muzzle) b.cyl("metal", "z", 0.05, 0.015, 0.015, [0, 0.0375, tip + 0.025]);
  for (const x of [0.0265, -0.0265]) {
    for (const z of [-0.12, -0.08, -0.04]) b.box("accent", [0.004, 0.014, 0.022], [x, 0.02, z]);
  }
  b.box("trim", [0.004, 0.024, 0.04], [0.0265, 0.03, 0.1]);
  guard(b, -0.07, -0.112, -0.085, -0.03);
  trigger(b, -0.085, -0.04);
  gripPrism(b, [[-0.03, -0.07], [0.025, -0.07], [0.04, -0.17], [-0.01, -0.17]], 0.034);
  b.box("body", [0.04, 0.012, 0.06], [0, -0.09, 0.14]);
  b.box("trim", [0.05, 0.15, 0.016], [0, -0.025, 0.308], { bevel: 0.003 });
  stickMag(mb, 0.03, 0.05, 0.14 * mv.lengthScale, mv.fast);
  return {
    mounts: {
      railTop: 0.076,
      opticZ: 0.02,
      barrelY: 0.0375,
      barrelR: 0.0105,
      muzzleZ: tip,
      underY: 0,
      underZ: -0.4,
      stockZ: null,
      stockY: 0,
    },
    iron: { rearZ: 0.14, rearBaseY: 0.076, frontZ: -0.44, frontBaseY: 0.075, sightY: 0.104 },
    builtinSight: null,
    rightGrip: [0, -0.12, 0.012],
    rightHold: "grip",
    leftGrip: [0, 0.0375, -0.38],
    leftHold: "cup",
    magPivot: [0, -0.096, 0.14],
  };
};

const hx12: Spec = (b, _mb, set) => {
  const ext = barrelDelta(set);
  const mv = magVariant(set);
  const blen = Math.max(0.52 + ext, 0.3);
  const tip = -0.14 - blen;
  b.box("body", [0.05, 0.075, 0.2], [0, 0.005, -0.04], { bevel: 0.006 });
  b.box("body", [0.022, 0.008, 0.16], [0, 0.0465, -0.04]);
  railSlots(b, 0.0505, -0.1, 0.02, 7);
  b.box("trim", [0.004, 0.022, 0.05], [0.026, 0.015, -0.04]);
  b.cyl("metal", "z", blen, 0.017, 0.017, [0, 0.035, -0.14 - blen / 2], { segments: 10 });
  const tubeEnd = mv.lengthScale > 1 ? tip + 0.02 : -0.54;
  const tubeLen = -0.14 - tubeEnd;
  b.cyl("metal", "z", tubeLen, 0.0135, 0.0135, [0, 0, -0.14 - tubeLen / 2]);
  b.cyl("metal", "z", 0.016, 0.016, 0.016, [0, 0, tubeEnd + 0.008]);
  if (mv.fast) b.cyl("accent", "z", 0.01, 0.0168, 0.0168, [0, 0, tubeEnd + 0.03]);
  b.box("body", [0.056, 0.048, 0.17], [0, 0, -0.3], { bevel: 0.006 });
  for (const z of [-0.35, -0.3, -0.25]) b.box("trim", [0.058, 0.05, 0.01], [0, 0, z]);
  if (!set.muzzle) b.cyl("accent", "z", 0.006, 0.018, 0.018, [0, 0.035, tip + 0.003]);
  guard(b, -0.0325, -0.075, -0.07, 0);
  trigger(b, -0.05, -0.01);
  gripPrism(b, [[0.03, -0.03], [0.085, -0.03], [0.1, -0.12], [0.05, -0.12]], 0.034);
  const mounts: Mounts = {
    railTop: 0.0505,
    opticZ: -0.03,
    barrelY: 0.035,
    barrelR: 0.017,
    muzzleZ: tip,
    underY: -0.024,
    underZ: -0.3,
    stockZ: 0.06,
    stockY: 0,
  };
  if (set.grip === "light_stock") {
    buildLightStock(b, mounts);
  } else {
    b.prism("body", [[0.06, 0.04], [0.33, 0.055], [0.35, -0.085], [0.07, -0.05]], 0.042, [0, 0, 0]);
    b.box("trim", [0.046, 0.15, 0.016], [0, -0.02, 0.352], { bevel: 0.003 });
  }
  return {
    mounts,
    iron: { rearZ: 0.03, rearBaseY: 0.0425, frontZ: tip + 0.02, frontBaseY: 0.052, sightY: 0.088 },
    builtinSight: null,
    rightGrip: [0, -0.075, 0.07],
    rightHold: "grip",
    leftGrip: [0, 0, -0.3],
    leftHold: "cup",
    magPivot: null,
  };
};

/** Marksman rifle: long, slim, thumbhole-style stock, folded bipod. */
const dm2: Spec = (b, mb, set) => {
  const ext = barrelDelta(set);
  const mv = magVariant(set);
  const blen = Math.max(0.26 + ext, 0.08);
  const tip = -0.48 - blen;
  b.box("body", [0.046, 0.07, 0.26], [0, 0.02, -0.05], { bevel: 0.006 });
  b.box("body", [0.022, 0.01, 0.62], [0, 0.06, -0.17]);
  railSlots(b, 0.065, -0.44, 0.1, 14);
  b.box("body", [0.05, 0.07, 0.3], [0, 0.02, -0.33], { bevel: 0.006 });
  for (let i = 0; i < 4; i++) b.box("trim", [0.052, 0.02, 0.012], [0, 0.02, -0.26 - i * 0.05]);
  b.cyl("metal", "z", blen, 0.0105, 0.0105, [0, 0.02, -0.48 - blen / 2]);
  if (!set.muzzle) {
    b.cyl("metal", "z", 0.06, 0.0155, 0.0155, [0, 0.02, tip + 0.03]);
    b.box("trim", [0.034, 0.006, 0.008], [0, 0.02, tip + 0.02]);
    b.box("trim", [0.034, 0.006, 0.008], [0, 0.02, tip + 0.04]);
  }
  guard(b, -0.015, -0.065, -0.065, 0);
  trigger(b, -0.04, -0.01);
  gripPrism(b, [[-0.02, -0.015], [0.045, -0.015], [0.065, -0.13], [0.005, -0.13]], 0.034);
  b.box("body", [0.04, 0.02, 0.06], [0, -0.025, -0.06]);
  stickMag(mb, 0.03, 0.05, 0.09 * mv.lengthScale, mv.fast);
  b.box("trim", [0.06, 0.014, 0.016], [0, -0.022, -0.37]);
  b.box("metal", [0.008, 0.008, 0.14], [0.02, -0.03, -0.43]);
  b.box("metal", [0.008, 0.008, 0.14], [-0.02, -0.03, -0.43]);
  const mounts: Mounts = {
    railTop: 0.065,
    opticZ: -0.05,
    barrelY: 0.02,
    barrelR: 0.0105,
    muzzleZ: tip,
    underY: -0.015,
    underZ: -0.38,
    stockZ: 0.08,
    stockY: 0,
  };
  if (set.grip === "light_stock") {
    buildLightStock(b, mounts);
  } else {
    b.prism("body", [[0.08, 0.05], [0.31, 0.06], [0.32, -0.08], [0.17, -0.09], [0.09, -0.05]], 0.04, [0, 0, 0]);
    b.box("body", [0.04, 0.02, 0.12], [0, 0.07, 0.21], { bevel: 0.004 });
    b.box("trim", [0.046, 0.13, 0.016], [0, -0.01, 0.328], { bevel: 0.003 });
  }
  return {
    mounts,
    iron: { rearZ: 0.1, rearBaseY: 0.065, frontZ: -0.47, frontBaseY: 0.055, sightY: 0.096 },
    builtinSight: null,
    rightGrip: [0, -0.075, 0.025],
    rightHold: "grip",
    leftGrip: [0, 0.02, -0.33],
    leftHold: "cup",
    magPivot: [0, -0.015, -0.06],
  };
};

/** Bolt-action sniper with a built-in 6x scope. */
const lx44: Spec = (b, mb, set) => {
  const ext = barrelDelta(set);
  const mv = magVariant(set);
  const slim = Math.max(0.2 + ext, 0.06);
  const tip = -0.58 - slim;
  b.box("body", [0.052, 0.075, 0.26], [0, 0.01, -0.06], { bevel: 0.006 });
  b.box("body", [0.046, 0.05, 0.24], [0, -0.002, -0.31], { bevel: 0.006 });
  b.cyl("metal", "z", 0.15, 0.0135, 0.0135, [0, 0, -0.505]);
  b.cyl("metal", "z", slim, 0.011, 0.011, [0, 0, -0.58 - slim / 2]);
  if (!set.muzzle) {
    b.cyl("metal", "z", 0.07, 0.017, 0.017, [0, 0, tip + 0.035]);
    for (const dz of [0.02, 0.035, 0.05]) b.box("trim", [0.036, 0.006, 0.008], [0, 0, tip + dz]);
  }
  // Built-in scope
  b.box("trim", [0.034, 0.056, 0.02], [0, 0.0755, -0.12]);
  b.box("trim", [0.034, 0.056, 0.02], [0, 0.0755, 0]);
  b.cyl("metal", "z", 0.34, 0.0205, 0.0205, [0, 0.1, -0.05], { segments: 10 });
  b.cyl("metal", "z", 0.07, 0.034, 0.0205, [0, 0.1, -0.255], { segments: 10 });
  b.cyl("metal", "z", 0.05, 0.0205, 0.03, [0, 0.1, 0.145], { segments: 10 });
  b.cyl("lens", "z", 0.002, 0.031, 0.031, [0, 0.1, -0.291], { segments: 10 });
  b.cyl("lens", "z", 0.002, 0.026, 0.026, [0, 0.1, 0.171], { segments: 10 });
  b.box("metal", [0.02, 0.022, 0.02], [0, 0.13, -0.07]);
  b.box("metal", [0.022, 0.02, 0.02], [0.03, 0.1, -0.07]);
  b.cyl("metal", "x", 0.02, 0.012, 0.012, [-0.032, 0.1, -0.17]);
  b.cyl("accent", "z", 0.01, 0.0215, 0.0215, [0, 0.1, -0.15]);
  // Bolt
  b.box("metal", [0.02, 0.02, 0.08], [0.03, 0.02, 0.04]);
  b.cyl("metal", "x", 0.07, 0.0055, 0.0055, [0.075, 0.02, 0.045], { segments: 6 });
  b.sphere("trim", 0.014, [0.115, 0.005, 0.05]);
  guard(b, -0.0275, -0.075, -0.07, 0);
  trigger(b, -0.05, -0.01);
  gripPrism(b, [[-0.02, -0.0275], [0.045, -0.0275], [0.065, -0.14], [0.005, -0.14]], 0.034);
  b.box("body", [0.04, 0.015, 0.07], [0, -0.035, -0.05]);
  stickMag(mb, 0.03, 0.06, 0.06 * mv.lengthScale, mv.fast);
  b.box("trim", [0.06, 0.014, 0.016], [0, -0.03, -0.36]);
  b.box("metal", [0.008, 0.008, 0.15], [0.022, -0.035, -0.42]);
  b.box("metal", [0.008, 0.008, 0.15], [-0.022, -0.035, -0.42]);
  const mounts: Mounts = {
    railTop: 0.0475,
    opticZ: -0.06,
    barrelY: 0,
    barrelR: 0.011,
    muzzleZ: tip,
    underY: -0.027,
    underZ: -0.26,
    stockZ: 0.07,
    stockY: 0,
  };
  if (set.grip === "light_stock") {
    buildLightStock(b, mounts);
  } else {
    b.prism("body", [[0.07, 0.04], [0.34, 0.06], [0.36, -0.1], [0.2, -0.11], [0.09, -0.04]], 0.045, [0, 0, 0]);
    b.box("trim", [0.042, 0.03, 0.13], [0, 0.075, 0.21], { bevel: 0.004 });
    b.box("trim", [0.052, 0.16, 0.026], [0, -0.02, 0.37], { bevel: 0.004 });
  }
  return {
    mounts,
    iron: null,
    builtinSight: { y: 0.1, z: 0.171 },
    rightGrip: [0, -0.085, 0.025],
    rightHold: "grip",
    leftGrip: [0, 0, -0.31],
    leftHold: "cup",
    magPivot: [0, -0.0275, -0.05],
  };
};

const p17: Spec = (b, mb, set) => {
  const ext = barrelDelta(set) * 0.5;
  const mv = magVariant(set);
  const tip = Math.min(-0.126 - ext, -0.112);
  b.box("metal", [0.032, 0.036, 0.19], [0, 0.02, -0.015], { bevel: 0.004 });
  for (const z of [0.05, 0.06, 0.07]) b.box("trim", [0.034, 0.03, 0.003], [0, 0.02, z]);
  b.box("trim", [0.004, 0.012, 0.045], [0.0165, 0.03, -0.02]);
  b.box("trim", [0.028, 0.022, 0.16], [0, -0.01, -0.03], { bevel: 0.003 });
  b.box("trim", [0.03, 0.01, 0.045], [0, -0.026, -0.085]);
  const nub = -0.11 - tip;
  if (nub > 0.002) b.cyl("metal", "z", nub, 0.0085, 0.0085, [0, 0.02, -0.11 - nub / 2]);
  gripPrism(b, [[-0.015, -0.021], [0.07, -0.021], [0.09, -0.135], [0.005, -0.135]], 0.03);
  guard(b, -0.021, -0.065, -0.062, 0);
  trigger(b, -0.04, -0.01);
  const mlen = 0.12 * mv.lengthScale;
  mb.box("trim", [0.022, mlen, 0.05], [0, -mlen / 2, 0]);
  mb.box(mv.fast ? "accent" : "trim", [0.032, 0.01, 0.062], [0, -mlen - 0.005, 0.004]);
  return {
    mounts: {
      railTop: 0.038,
      opticZ: 0.01,
      barrelY: 0.02,
      barrelR: 0.0085,
      muzzleZ: tip,
      underY: -0.031,
      underZ: -0.085,
      stockZ: null,
      stockY: 0,
    },
    iron: { rearZ: 0.07, rearBaseY: 0.038, frontZ: -0.095, frontBaseY: 0.038, sightY: 0.056 },
    builtinSight: null,
    rightGrip: [0, -0.06, 0.04],
    rightHold: "grip",
    leftGrip: [0, -0.105, 0.045],
    leftHold: "grip",
    magPivot: [0, -0.021, 0.047],
  };
};

/** Machine pistol: boxy slide, ported compensator block, built-in vertical foregrip, arm brace. */
const vp4: Spec = (b, mb, set) => {
  const ext = barrelDelta(set) * 0.5;
  const mv = magVariant(set);
  const tip = Math.min(-0.182 - ext, -0.176);
  b.box("metal", [0.034, 0.04, 0.2], [0, 0.02, -0.03], { bevel: 0.004 });
  for (const z of [-0.1, -0.085, -0.07]) b.box("trim", [0.02, 0.004, 0.01], [0, 0.04, z]);
  b.box("trim", [0.004, 0.014, 0.05], [0.0175, 0.03, -0.02]);
  b.box("trim", [0.03, 0.026, 0.17], [0, -0.016, -0.03], { bevel: 0.003 });
  b.box("metal", [0.032, 0.032, 0.04], [0, 0.02, -0.15], { bevel: 0.003 });
  b.box("trim", [0.034, 0.02, 0.005], [0, 0.02, -0.145]);
  b.box("trim", [0.034, 0.02, 0.005], [0, 0.02, -0.16]);
  const nub = -0.17 - tip;
  if (nub > 0.002) b.cyl("metal", "z", nub, 0.008, 0.008, [0, 0.02, -0.17 - nub / 2]);
  gripPrism(b, [[-0.005, -0.029], [0.065, -0.029], [0.085, -0.125], [0.015, -0.125]], 0.03);
  b.box("trim", [0.026, 0.07, 0.028], [0, -0.065, -0.085], { bevel: 0.004 });
  b.box("accent", [0.028, 0.006, 0.03], [0, -0.095, -0.085]);
  guard(b, -0.029, -0.07, -0.045, 0.005);
  trigger(b, -0.05, -0.015);
  b.box("metal", [0.008, 0.008, 0.1], [0.016, 0.02, 0.12]);
  b.box("metal", [0.008, 0.008, 0.1], [-0.016, 0.02, 0.12]);
  b.box("trim", [0.05, 0.03, 0.01], [0, 0.02, 0.175]);
  const mlen = 0.15 * mv.lengthScale;
  mb.box("trim", [0.024, mlen, 0.052], [0, -mlen / 2, 0]);
  mb.box(mv.fast ? "accent" : "trim", [0.034, 0.01, 0.062], [0, -mlen - 0.005, 0]);
  return {
    mounts: {
      railTop: 0.04,
      opticZ: -0.01,
      barrelY: 0.02,
      barrelR: 0.008,
      muzzleZ: tip,
      underY: -0.029,
      underZ: -0.125,
      stockZ: null,
      stockY: 0,
    },
    iron: { rearZ: 0.055, rearBaseY: 0.04, frontZ: -0.12, frontBaseY: 0.04, sightY: 0.06 },
    builtinSight: null,
    rightGrip: [0, -0.075, 0.035],
    rightHold: "grip",
    leftGrip: [0, -0.065, -0.085],
    leftHold: "grip",
    magPivot: [0, -0.029, 0.04],
  };
};

/** Revolver: hex drum, heavy barrel with underlug (camo), big grip. */
const hc50: Spec = (b, _mb, set) => {
  const ext = barrelDelta(set);
  const blen = Math.max(0.2 + ext, 0.1);
  const tip = -0.076 - blen;
  b.box("metal", [0.032, 0.07, 0.1], [0, 0.015, 0.02], { bevel: 0.005 });
  b.box("metal", [0.026, 0.01, 0.135], [0, 0.055, 0.0075]);
  b.cyl("metal", "z", 0.072, 0.032, 0.032, [0, 0.012, -0.04], { segments: 6 });
  b.cyl("metal", "z", blen, 0.0145, 0.0145, [0, 0.035, -0.076 - blen / 2]);
  const lug = blen * 0.75;
  b.cyl("body", "z", lug, 0.011, 0.011, [0, 0.008, -0.076 - lug / 2]);
  b.box("body", [0.008, 0.008, blen], [0, 0.0535, -0.076 - blen / 2]);
  b.box("metal", [0.012, 0.036, 0.014], [0, 0.075, 0.075], { rot: [-0.45, 0, 0] });
  b.prism(
    "body",
    [[0.025, 0.01], [0.085, 0.02], [0.108, -0.055], [0.098, -0.13], [0.04, -0.14], [0.015, -0.07]],
    0.034,
    [0, 0, 0],
  );
  guard(b, -0.02, -0.07, -0.045, 0.02);
  trigger(b, -0.045, -0.012);
  if (!set.muzzle) b.cyl("metal", "z", 0.012, 0.0165, 0.0165, [0, 0.035, tip + 0.006]);
  return {
    mounts: {
      railTop: 0.06,
      opticZ: 0,
      barrelY: 0.035,
      barrelR: 0.0145,
      muzzleZ: tip,
      underY: -0.003,
      underZ: -0.15,
      stockZ: null,
      stockY: 0,
    },
    iron: { rearZ: 0.065, rearBaseY: 0.06, frontZ: tip + 0.02, frontBaseY: 0.058, sightY: 0.085 },
    builtinSight: null,
    rightGrip: [0, -0.05, 0.066],
    rightHold: "grip",
    leftGrip: [0, -0.1, 0.07],
    leftHold: "grip",
    magPivot: null,
  };
};

/** Runner Knife: swept blade, red wrapped handle. Ignores attachments. */
const knife: Spec = (b) => {
  b.prism(
    "metal",
    [[-0.026, 0.016], [-0.2, 0.016], [-0.25, -0.002], [-0.2, -0.014], [-0.026, -0.014]],
    0.005,
    [0, 0, 0],
  );
  b.box("body", [0.0056, 0.004, 0.12], [0, 0.006, -0.11]);
  b.box("trim", [0.03, 0.034, 0.11], [0, 0, 0.04], { bevel: 0.005 });
  for (const z of [0.015, 0.04, 0.065]) b.box("accent", [0.032, 0.036, 0.008], [0, 0, z]);
  b.box("metal", [0.05, 0.04, 0.012], [0, 0, -0.02], { bevel: 0.003 });
  b.box("metal", [0.032, 0.036, 0.012], [0, 0, 0.1], { bevel: 0.003 });
  return {
    mounts: null,
    iron: null,
    builtinSight: null,
    rightGrip: [0, 0, 0.04],
    rightHold: "handle",
    leftGrip: [0, 0, 0.04],
    leftHold: "none",
    magPivot: null,
    muzzle: [0, 0, -0.25],
  };
};

const SPECS: Readonly<Record<string, Spec>> = { vx9, kr7, br3, hx12, dm2, lx44, p17, vp4, hc50, knife };

// ---------------------------------------------------------------------------------------------
// Assembly

function assemble(id: string, attachments: readonly string[]): Assembly {
  const spec: Spec | undefined = SPECS[id];
  if (spec === undefined) throw new Error(`unknown weapon "${id}"`);
  const set: AttachmentSet = id === "knife" ? {} : parseAttachments(attachments);
  const body = new MeshBuilder();
  const mag = new MeshBuilder();
  const r = spec(body, mag, set);
  let sight: V3 = [0, 0.03, 0];
  let hasScopeOverlay = false;
  let muzzle: V3 = r.muzzle ?? [0, 0, -0.5];
  if (r.mounts !== null) {
    const m = r.mounts;
    if (set.optic !== undefined) {
      const o = buildOptic(body, set.optic, m);
      sight = [0, o.y, o.z];
      hasScopeOverlay = o.scope;
    } else if (r.builtinSight !== null) {
      sight = [0, r.builtinSight.y, r.builtinSight.z];
      hasScopeOverlay = true;
    } else if (r.iron !== null) {
      ironSights(body, r.iron);
      sight = [0, r.iron.sightY, r.iron.rearZ];
    }
    const muzzleZ = set.muzzle !== undefined ? buildMuzzle(body, set.muzzle, m) : m.muzzleZ;
    muzzle = [0, m.barrelY, muzzleZ];
    if (set.grip !== undefined) buildGrip(body, set.grip, m);
  }
  return {
    body,
    mag,
    layout: {
      rightGrip: r.rightGrip,
      rightHold: r.rightHold,
      leftGrip: r.leftGrip,
      leftHold: r.leftHold,
      muzzle,
      sight,
      magPivot: r.magPivot,
      hasScopeOverlay,
      twoHanded: id !== "knife",
    },
  };
}

/** Geometry-free description of a loadout (anchors, triangle count); throws for unknown weapon ids. */
export function describeWeapon(id: string, attachments: readonly string[] = []): WeaponDescription {
  const a = assemble(id, attachments);
  return { layout: a.layout, triangles: a.body.triangleCount() + a.mag.triangleCount() };
}

function anchor(root: THREE.Object3D, name: string, p: V3): THREE.Object3D {
  const o = new THREE.Object3D();
  o.name = name;
  o.position.set(p[0], p[1], p[2]);
  root.add(o);
  return o;
}

/**
 * Builds a weapon model. `attachments` may list at most one id per slot (extra ids of an occupied slot
 * and unknown ids are ignored); `camo` is a gr-core CamoId ("none" for the plain polymer finish).
 * Throws for unknown weapon ids.
 */
export function createWeaponModel(id: string, attachments: string[], camo: string): WeaponModel {
  const asm = assemble(id, attachments);
  const mats = createWeaponMaterials(camo);
  const uvMeters = { body: mats.bodyUvMeters, metal: 0.3 };
  const geos: THREE.BufferGeometry[] = [];
  const root = new THREE.Group();
  root.name = `weapon:${id}`;

  const addMeshes = (parent: THREE.Object3D, builder: MeshBuilder): void => {
    for (const [key, geo] of Object.entries(builder.build({ uvMeters }))) {
      const material = mats.mats[key as WeaponMaterialKey];
      if (material === undefined) throw new Error(`weapon ${id}: no material for key "${key}"`);
      geos.push(geo);
      const mesh = new THREE.Mesh(geo, material);
      mesh.name = `${id}:${key}`;
      parent.add(mesh);
    }
  };
  addMeshes(root, asm.body);

  let magazine: THREE.Group | null = null;
  const l = asm.layout;
  if (l.magPivot !== null && asm.mag.triangleCount() > 0) {
    magazine = new THREE.Group();
    magazine.name = "magazine";
    magazine.position.set(l.magPivot[0], l.magPivot[1], l.magPivot[2]);
    addMeshes(magazine, asm.mag);
    root.add(magazine);
  }

  const muzzle = anchor(root, "muzzle", l.muzzle);
  const sightAnchor = anchor(root, "sightAnchor", l.sight);
  const rightHandGrip = anchor(root, "rightHandGrip", l.rightGrip);
  const leftHandGrip = anchor(root, "leftHandGrip", l.leftGrip);
  rightHandGrip.userData.hold = l.rightHold;
  leftHandGrip.userData.hold = l.leftHold;

  return {
    id,
    root,
    muzzle,
    magazine,
    sightAnchor,
    leftHandGrip,
    rightHandGrip,
    hasScopeOverlay: l.hasScopeOverlay,
    twoHanded: l.twoHanded,
    dispose(): void {
      root.removeFromParent();
      disposeGeometries(geos);
      mats.dispose();
    },
  };
}
